/**
 * Button Component
 * 
 * A versatile button component with multiple variants and sizes.
 * Uses the tailwind-utils for consistent styling.
 */
import { ButtonHTMLAttributes } from 'react'
import { cn } from '../../utils/tailwind-utils'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The visual style variant of the button
   */
  variant?: 'primary' | 'secondary' | 'accent' | 'destructive' | 'outline' | 'ghost' | 'link'
  
  /**
   * The size of the button
   */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Button component with various style variants and sizes
 * 
 * @example
 * // Primary button (default)
 * <Button>Click me</Button>
 * 
 * @example
 * // Secondary button with small size
 * <Button variant="secondary" size="sm">Small Button</Button>
 * 
 * @example
 * // Outline button with custom class
 * <Button variant="outline" className="w-full">Full Width</Button>
 */
function Button({
  className,
  variant = 'primary',
  size = 'md',
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:pointer-events-none',
        variant === 'primary' && 'bg-primary text-primary-foreground hover:bg-primary/90',
        variant === 'destructive' && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        variant === 'outline' && 'border border-input hover:bg-accent hover:text-accent-foreground',
        variant === 'secondary' && 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        variant === 'ghost' && 'hover:bg-accent hover:text-accent-foreground',
        variant === 'link' && 'underline-offset-4 hover:underline text-primary',
        size === 'sm' && 'h-9 px-3',
        size === 'md' && 'h-10 py-2 px-4',
        size === 'lg' && 'h-11 px-8',
        className
      )}
      {...props}
    />
  )
}

export default Button
